import React from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

export const StatusChip = ({ tone = "neutral", children, className = "", ...rest }) => {
  const tones = {
    neutral: "bg-white/5 text-[--tx-2] border-white/10",
    ai: "bg-[--ai-soft] text-[--ai] border-white/10",
    risk: "bg-[--amber-soft] text-[--amber] border-white/10",
    validated: "bg-[--green-soft] text-[--green] border-white/10",
    danger: "bg-[--danger-soft] text-[--danger] border-white/10",
    crimson: "bg-[--six-crimson-soft] text-[#E86A6A] border-white/10",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap ${tones[tone]} ${className}`} {...rest}>
      {children}
    </span>
  );
};

export const ConfidenceBadge = ({ level, className = "" }) => {
  const l = (level || "").toLowerCase();
  let tone = "ai";
  let label = level;
  if (l.includes("evidence")) { tone = "validated"; label = "Evidence-backed"; }
  else if (l.includes("hypothesis")) { tone = "ai"; label = "Hypothesis"; }
  else if (l.includes("missing") || l.includes("data missing")) { tone = "risk"; label = "Data missing"; }
  else if (l.includes("high")) { tone = "validated"; }
  else if (l.includes("medium") || l.includes("moderate")) { tone = "ai"; }
  else if (l.includes("low")) { tone = "risk"; }
  else if (l.includes("illustrative")) { tone = "risk"; label = "Illustrative"; }
  return <StatusChip tone={tone} className={className} data-testid="confidence-badge">{label}</StatusChip>;
};

export const SeverityChip = ({ severity }) => {
  const s = (severity || "").toLowerCase();
  const tone = s.includes("critical") ? "danger" : s.includes("high") ? "risk" : "neutral";
  return <StatusChip tone={tone}>{severity}</StatusChip>;
};

export const DemoBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[--amber-soft] text-[--amber] border border-white/10 ${className}`} data-testid="demo-data-badge">
    Illustrative Demo Data
  </span>
);

export const KpiTile = ({ label, value, sub, tone = "neutral", testId }) => {
  const accent = { neutral: "text-[--tx-1]", ai: "text-[--ai]", risk: "text-[--amber]", validated: "text-[--green]", danger: "text-[--danger]", crimson: "text-[#E86A6A]" }[tone];
  return (
    <div className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5 min-w-0" data-testid={testId || "kpi-tile"}>
      <div className="text-[11px] uppercase tracking-wider text-[--tx-3] font-medium truncate">{label}</div>
      <div className={`font-display text-2xl md:text-3xl font-semibold tabular-nums mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-[--tx-3] mt-1 leading-snug">{sub}</div>}
    </div>
  );
};

export const PageHeader = ({ kicker, title, subtitle, right, testId }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-5" data-testid={testId || "page-header"}>
    <div className="min-w-0">
      {kicker && <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#E86A6A] mb-1">{kicker}</div>}
      <h1 className="font-display text-xl md:text-2xl font-semibold text-[--tx-1] leading-tight">{title}</h1>
      {subtitle && <p className="text-sm text-[--tx-2] mt-1 max-w-3xl leading-relaxed">{subtitle}</p>}
    </div>
    {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
  </div>
);

export const Panel = ({ title, right, children, className = "", testId }) => (
  <div className={`rounded-xl border border-[--hairline] bg-[--panel-dark] ${className}`} data-testid={testId}>
    {(title || right) && (
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2 border-b border-[--divider]">
        <h3 className="text-[13px] font-semibold text-[--tx-1]">{title}</h3>
        {right}
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

export const PersonaStamp = ({ code, size = "md" }) => {
  const colors = { TP: "border-[#E86A6A] text-[#E86A6A]", DS: "border-[--ai] text-[--ai]", TO: "border-[--tx-2] text-[--tx-2]" };
  const sz = size === "sm" ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-xs";
  return (
    <div className={`${sz} rounded-md border-2 ${colors[code] || colors.TO} flex items-center justify-center font-display font-bold shrink-0 bg-[--shell-1]`}>
      {code}
    </div>
  );
};

export const CopyButton = ({ text, label = "Copy", testId }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button
      onClick={handleCopy}
      data-testid={testId || "copy-button"}
      className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1.5 text-xs font-medium text-[--tx-2] hover:bg-[#243041] hover:text-[--tx-1] active:scale-[0.98] transition-colors"
    >
      {copied ? <Check size={13} className="text-[--green]" /> : <Copy size={13} />}
      {copied ? "Copied" : label}
    </button>
  );
};

export const EmptyState = ({ message }) => (
  <div className="py-10 text-center text-sm text-[--tx-3]" data-testid="empty-state">{message}</div>
);

export const LoadingState = ({ message = "Loading workspace..." }) => (
  <div className="flex items-center justify-center py-16" data-testid="loading-state">
    <div className="flex items-center gap-3 text-sm text-[--tx-3]">
      <span className="inline-block w-4 h-4 rounded-full border-2 border-[--ai] border-t-transparent animate-spin" />
      {message}
    </div>
  </div>
);

export const outcomeLabel = (key) => ({
  on_time: "On time", on_budget: "On budget", on_spec: "On spec",
  no_surprises: "No surprises", customer_experience: "Outstanding CX", business_benefits: "Business benefits",
}[key] || key);

export const OutcomeChip = ({ outcome }) => (
  <StatusChip tone="crimson" className="!text-[10px]">{outcomeLabel(outcome)}</StatusChip>
);
